import {
  useGetReportsCustomerInsights,
  useGetReportsSalesStats,
  useGetReportsTopProducts,
} from '@agos/api-client/reporting'
import { T, formatCents, formatCount, formatDay, tt } from '@agos/shared'
import { DataTable, EmptyState, Spinner, cn, type Column } from '@agos/ui'
import { BarChart3, Package, Users } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  CUSTOMER_DAY_WINDOWS,
  DAY_WINDOWS,
  customerInsightsOf,
  emptyWindowNote,
  salesReportOf,
  topProductsOf,
  type SalesCut,
  type TopProduct,
} from './sales-reports'

/**
 * *Predaj*, *Produkty* and *Zákazníci* — §9.251, FB-411, the owner's second decision.
 *
 * Three endpoints served since the port and read by nothing, and three tab components legacy defines
 * and never renders. FB-400 recommended leaving them alone; the owner was asked and said build them.
 *
 * **Everything printed is the server's own figure.** Nothing here sums, averages or re-derives, which
 * is what keeps these tabs from disagreeing with *Štatistiky* over the same period — §9.211's rule
 * about a second list computing the same total a second way.
 *
 * Their own file, as `OrderHistoryCard` and `SavedAddressRow` are: `ReportsPage.tsx` is 1 500 lines
 * and has enough tabs in it already.
 */

function DaysSelector({
  value,
  onChange,
  options,
}: {
  value: number
  onChange: (days: number) => void
  options: readonly number[]
}) {
  return (
    <div className="flex flex-wrap gap-1.5" data-days={value}>
      {options.map((days) => (
        <button
          key={days}
          type="button"
          data-days-option={days}
          aria-pressed={value === days}
          onClick={() => onChange(days)}
          className={cn(
            'px-3 py-1.5 rounded-lg text-xs font-bold border transition-all',
            value === days
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-card border-border text-muted-foreground hover:text-foreground',
          )}
        >
          {formatCount(days)} <T>dní</T>
        </button>
      ))}
    </div>
  )
}

/** Legacy's bar rows, over a cut the server produced. */
function CutRows({ rows, testId }: { rows: SalesCut[]; testId: string }) {
  const largest = rows.reduce((top, row) => Math.max(top, row.revenueCents), 0)
  if (rows.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-cut-empty={testId}>
        <T>Žiadne dáta</T>
      </p>
    )
  }
  return (
    <div className="space-y-2" data-cut={testId}>
      {rows.map((row) => (
        <div key={row.key} className="space-y-1" data-cut-row={row.key}>
          <div className="flex items-baseline justify-between gap-3 text-xs">
            <span className="font-medium truncate">{row.label}</span>
            <span className="tabular-nums font-semibold shrink-0">
              {formatCents(row.revenueCents)}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary"
              style={{ width: `${largest === 0 ? 0 : (row.revenueCents / largest) * 100}%` }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            {formatCount(row.orderCount)} <T>obj.</T>
            {row.avgOrderCents !== null && ` · Ø ${formatCents(row.avgOrderCents)}`}
          </p>
        </div>
      ))}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
      <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  )
}

export function SalesTab() {
  const [days, setDays] = useState(30)
  const { data, isLoading } = useGetReportsSalesStats({ days })
  const report = useMemo(() => salesReportOf(data), [data])

  return (
    <div className="space-y-4" data-sales-report>
      <DaysSelector value={days} onChange={setDays} options={DAY_WINDOWS} />
      {isLoading ? (
        <Spinner />
      ) : report.isEmpty ? (
        // FB-260's rule: an aged window reads as a broken screen unless it says which it is. The
        // restored dump's newest order is 2026-07-28, so thirty days answers nothing.
        <EmptyState
          icon={<BarChart3 className="w-8 h-8" />}
          title={tt('Za toto obdobie nič')}
          description={emptyWindowNote(days)}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* The cut nothing else in the manager offers. */}
          <Panel title={tt('Podľa predajného kanála')}>
            <CutRows rows={report.byChannel} testId="channel" />
          </Panel>
          <Panel title={tt('Podľa spôsobu platby')}>
            <CutRows rows={report.byPayment} testId="payment" />
          </Panel>
          <Panel title={tt('Podľa dňa v týždni')}>
            <CutRows rows={report.byWeekday} testId="weekday" />
          </Panel>
          <Panel title={tt('Podľa hodiny')}>
            <CutRows rows={report.byHour} testId="hour" />
          </Panel>
        </div>
      )}
    </div>
  )
}

export function TopProductsTab() {
  const [days, setDays] = useState(30)
  const { data, isLoading } = useGetReportsTopProducts({ days, limit: 30 })
  const rows = useMemo(() => topProductsOf(data), [data])

  const columns: Column<TopProduct>[] = [
    {
      key: 'name',
      header: tt('Produkt'),
      render: (row) => <span className="font-medium">{row.name}</span>,
      sortable: true,
      sortValue: (row) => row.name,
    },
    {
      key: 'category',
      header: tt('Kategória'),
      render: (row) => row.categoryName ?? '-',
      sortable: true,
      sortValue: (row) => row.categoryName ?? '',
    },
    {
      key: 'quantity',
      header: tt('Predané'),
      className: 'text-right tabular-nums',
      render: (row) => formatCount(row.quantity),
      sortable: true,
      sortValue: (row) => row.quantity,
    },
    {
      key: 'revenue',
      header: tt('Tržba'),
      className: 'text-right tabular-nums font-semibold',
      render: (row) => formatCents(row.revenueCents),
      sortable: true,
      sortValue: (row) => row.revenueCents,
    },
  ]

  return (
    <div className="space-y-4" data-top-products={rows.length}>
      <DaysSelector value={days} onChange={setDays} options={DAY_WINDOWS} />
      {/* Said once, where somebody comparing two screens would be standing: this list is per
          **product**, and *Štatistiky* lists the same sales per **size variant** — 4 232 rows against
          this one's thirty. Two questions, not two answers. */}
      <p className="text-[11px] text-muted-foreground">
        <T>
          Zoradené podľa počtu predaných kusov. Štatistiky ukazujú tie isté predaje po veľkostiach.
        </T>
      </p>
      {isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Package className="w-8 h-8" />}
          title={tt('Za toto obdobie nič')}
          description={emptyWindowNote(days)}
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(row) => String(row.productId)}
          rowAttributes={(row) => ({ 'data-top-product': String(row.productId) })}
          emptyMessage={tt('Žiadne dáta')}
          pageSize={30}
          compact
        />
      )}
    </div>
  )
}

export function CustomersTab() {
  const [days, setDays] = useState(90)
  const { data, isLoading } = useGetReportsCustomerInsights({ days })
  const insights = useMemo(() => customerInsightsOf(data), [data])

  return (
    <div className="space-y-4" data-customer-insights>
      <DaysSelector value={days} onChange={setDays} options={CUSTOMER_DAY_WINDOWS} />
      {isLoading ? (
        <Spinner />
      ) : insights.totalOrders === 0 ? (
        <EmptyState
          icon={<Users className="w-8 h-8" />}
          title={tt('Za toto obdobie nič')}
          description={emptyWindowNote(days)}
        />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-insight-tiles>
            <Tile label={tt('Zákazníci')} value={formatCount(insights.uniqueCustomers)} />
            <Tile
              label={tt('Vracajúci sa')}
              value={formatCount(insights.returningCustomers)}
              // The server's own percentage, printed rather than recomputed from the two counts —
              // they are over the same window but not necessarily the same denominator.
              sub={`${insights.returningPercent} %`}
            />
            <Tile label={tt('Objednávky')} value={formatCount(insights.totalOrders)} />
            <Tile label={tt('Priemerná objednávka')} value={formatCents(insights.avgOrderCents)} />
          </div>

          <Panel title={tt('Najväčší zákazníci')}>
            <div className="divide-y divide-border" data-top-spenders={insights.topSpenders.length}>
              {insights.topSpenders.map((spender) => (
                <div
                  key={spender.phone}
                  className="flex items-center gap-3 py-2 text-sm"
                  data-top-spender={spender.phone}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block truncate font-medium">{spender.name}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {formatCount(spender.orderCount)} <T>obj.</T>
                      {spender.lastOrderAt !== null &&
                        ` · ${tt('naposledy')} ${formatDay(spender.lastOrderAt.slice(0, 10))}`}
                    </span>
                  </span>
                  <span className="tabular-nums font-semibold shrink-0">
                    {formatCents(spender.totalSpentCents)}
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  )
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      {sub !== undefined && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  )
}
